import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatKop } from '../../lib/money';
import { plural } from '../../lib/plural';
import { Notice, Opt } from '../../ui/parts';
import { useBooking } from '../../client/BookingContext';
import { useCreateReferral, useIntake } from '../api';
import { UNSURE, useOnboarding } from '../OnboardingContext';

/**
 * Первый вход, итог. Две ветки: записаться на первичный приём самому или
 * оставить заявку администратору. Заявка уходит администратору всегда,
 * выбранному специалисту — дополнительно.
 */
export function Result() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const { nb, patch } = useOnboarding();
  const { reset: resetBooking } = useBooking();
  const referral = useCreateReferral();
  const direct = nb.directionId !== null && nb.directionId !== UNSURE;
  const intake = useIntake(direct ? nb.directionId : null);

  const service = intake.data?.service ?? null;
  const specialists = intake.data?.specialists ?? [];
  const chosen = specialists.find((s) => s.id === nb.specialistId) ?? null;

  useScreen('Первая запись', {
    label: direct ? 'Выбрать время' : referral.isPending ? 'Отправляем…' : 'Отправить заявку',
    disabled: direct ? !chosen : referral.isPending,
    onClick: () => (direct ? toCalendar() : sendReferral(null)),
  });

  function toCalendar() {
    if (!chosen || !service || !nb.childId) return;
    resetBooking({
      flow: 'intake',
      childId: nb.childId,
      specialistId: chosen.id,
      specialistName: chosen.name,
      serviceId: service.id,
      serviceTitle: service.title,
      durationMin: service.durationMin,
      priceKop: chosen.priceKop,
      isOnline: service.isOnline,
      intake: { name: nb.name, age: nb.age, note: nb.note, phone: nb.phone, consent: true },
    });
    navigate('/calendar');
  }

  function sendReferral(preferredSpecialistId: number | null) {
    if (!nb.childId) {
      toast('Сначала заполните карточку ребёнка');
      navigate('/onboarding/child', { replace: true });
      return;
    }
    referral.mutate(
      {
        childId: nb.childId,
        directionId: direct ? nb.directionId : null,
        preferredSpecialistId,
        note: nb.note.trim(),
      },
      {
        onSuccess: () => {
          const name = preferredSpecialistId
            ? specialists.find((s) => s.id === preferredSpecialistId)?.name ?? null
            : null;
          patch({ sentToName: name });
          navigate('/onboarding/sent', { replace: true });
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отправить заявку'),
      },
    );
  }

  if (!direct) {
    return (
      <Screen>
        <div>
          <p className="eyebrow">Заявка администратору</p>
          <p className="lead" style={{ marginTop: 6 }}>
            Мы передадим, что вы написали, и подберём специалиста.
          </p>
        </div>
        <dl className="summary">
          <div>
            <dt>Ребёнок</dt>
            <dd>{nb.name}{nb.age ? `, ${nb.age} ${plural(Number(nb.age), 'год', 'года', 'лет')}` : ''}</dd>
          </div>
          <div>
            <dt>Запрос</dt>
            <dd style={{ maxWidth: '60%' }}>{nb.note.trim() || 'не указан'}</dd>
          </div>
          <div>
            <dt>Связь</dt>
            <dd>{nb.phone}</dd>
          </div>
        </dl>
        <Notice kind="calm">
          Ответ обычно приходит в течение дня. Если срочно — позвоните: {me.center.phone}.
        </Notice>
      </Screen>
    );
  }

  return (
    <Screen>
      {intake.isPending ? <p className="lead">Смотрим первую запись…</p> : null}

      {service ? (
        <>
          <div>
            <p className="eyebrow">{service.title}</p>
            <p className="lead" style={{ marginTop: 6 }}>
              С этого начинают все: {service.durationMin} минут, разбор результатов с родителем.
              Дальше специалист скажет, нужны ли регулярные записи.
            </p>
          </div>

          <p className="eyebrow">Кто принимает</p>
          <div className="options">
            {specialists.map((s) => (
              <Opt
                key={s.id}
                pressed={nb.specialistId === s.id}
                title={s.name}
                aside={<span>{formatKop(s.priceKop)}</span>}
                note={s.title}
                onClick={() => patch({ specialistId: s.id })}
              />
            ))}
          </div>

          <Notice kind="calm">
            После первой встречи специалист закрепится за ребёнком, и записываться станет
            проще — сразу к нему, без выбора.
          </Notice>

          <p className="eyebrow">Не нашли удобное время?</p>
          <button className="btn" disabled={referral.isPending} onClick={() => sendReferral(nb.specialistId)}>
            {chosen
              ? `Оставить заявку — свяжутся администратор и ${chosen.name}`
              : 'Оставить заявку, администратор подберёт время и специалиста'}
          </button>
        </>
      ) : null}
    </Screen>
  );
}
